// import React, { useState } from "react";
// import { Drawer, Form, Input, Button, message } from "antd";
// import axios from "axios";
// import Swal from "sweetalert2";
// import { API_BASE_CRM_URL } from "../../Config/Config";

// const { TextArea } = Input;

// const AddNote = ({ open, onClose, customerId, addedFrom, onSuccess }) => {
//   const [form] = Form.useForm();
//   const [loading, setLoading] = useState(false);

//   const handleSubmit = async (values) => {
//     try {
//       setLoading(true);

//       const payload = {
//         rel_id: customerId,
//         rel_type: "Customer",
//         description: values.description,
//         addedfrom: addedFrom,
//       };

//       const response = await axios.post(
//         `${API_BASE_CRM_URL}notes/AddNote`,
//         payload,
//       );

//       if (response.data.Status) {
//         Swal.fire({
//           icon: "success",
//           title: "Success",
//           text: response.data.Message,
//           confirmButtonColor: "#1B2850",
//         });

//         form.resetFields();

//         if (onSuccess) {
//           onSuccess();
//         }

//         onClose();
//       } else {
//         Swal.fire({
//           icon: "error",
//           title: "Error",
//           text: response.data.Message,
//           confirmButtonColor: "#1B2850",
//         });
//       }
//     } catch (error) {
//       console.error(error);

//       Swal.fire({
//         icon: "error",
//         title: "Failed",
//         text: error?.response?.data?.Message || "Failed to add note",
//         confirmButtonColor: "#1B2850",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Drawer
//       title={
//         <div className="d-flex align-items-center">
//           <i
//             className="fa-solid fa-note-sticky me-2"
//             style={{ color: "#1B2850" }}
//           ></i>
//           Add Customer Note
//         </div>
//       }
//       placement="bottom"
//       height="45%"
//       open={open}
//       onClose={onClose}
//       destroyOnClose
//     >
//       <Form form={form} layout="vertical" onFinish={handleSubmit}>
//         {/* <Form.Item label="Customer ID">
//           <Input value={customerId} disabled />
//         </Form.Item> */}

//         <Form.Item
//           label="Notes"
//           name="description"
//           rules={[
//             {
//               required: true,
//               message: "Please enter notes",
//             },
//           ]}
//         >
//           <TextArea
//             rows={6}
//             placeholder="Enter customer notes..."
//             maxLength={1000}
//             showCount
//           />
//         </Form.Item>

//         <div className="d-flex justify-content-end">
//           <Button className="me-2" onClick={onClose}>
//             Cancel
//           </Button>

//           <Button type="primary" htmlType="submit" loading={loading}>
//             Add Note
//           </Button>
//         </div>
//       </Form>
//     </Drawer>
//   );
// };

// export default AddNote;

import React, { useState, useRef, useEffect } from "react";
import {
  Drawer,
  Form,
  Input,
  Button,
  Upload,
  Tag,
  Space,
  message,
  Tooltip,
} from "antd";

import {
  PaperClipOutlined,
  CameraOutlined,
  AudioOutlined,
  DeleteOutlined,
  LoadingOutlined,
  CloseOutlined,
  CheckOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";

import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE_CRM_URL } from "../../Config/Config";

const { TextArea } = Input;

const AddNote = ({ open, onClose, customerId, addedFrom, onSuccess }) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  // Attachments
  const [fileList, setFileList] = useState([]);

  // Recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  // Voice preview
  const [voicePreview, setVoicePreview] = useState(null);
  const [voiceFile, setVoiceFile] = useState(null);

  // Refs for MediaRecorder
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Cleanup audio URL
  useEffect(() => {
    return () => {
      if (voicePreview) {
        URL.revokeObjectURL(voicePreview);
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [voicePreview]);

  // ---------------------------------------
  // Normal file upload
  // ---------------------------------------
  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // ---------------------------------------
  // Start recording
  // ---------------------------------------
  const startRecording = async () => {
    try {
      // Remove previous preview
      if (voicePreview) {
        URL.revokeObjectURL(voicePreview);
      }

      setVoicePreview(null);
      setVoiceFile(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const recorder = new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });

        const newVoiceFile = new File(
          [audioBlob],
          `voice_note_${Date.now()}.wav`,
          {
            type: "audio/wav",
          },
        );

        // Create preview URL
        const previewUrl = URL.createObjectURL(audioBlob);

        setVoiceFile(newVoiceFile);
        setVoicePreview(previewUrl);

        // Stop microphone
        stream.getTracks().forEach((track) => track.stop());

        setRecordingTime(0);
      };

      recorder.start();

      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Microphone access denied:", error);

      message.error(
        "Could not access microphone. Please allow microphone permission.",
      );
    }
  };

  // ---------------------------------------
  // Stop recording
  // ---------------------------------------
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      setIsRecording(false);
    }
  };

  // ---------------------------------------
  // Delete voice recording
  // ---------------------------------------
  const removeVoiceRecording = () => {
    if (voicePreview) {
      URL.revokeObjectURL(voicePreview);
    }

    setVoicePreview(null);
    setVoiceFile(null);
    setRecordingTime(0);
  };

  // ---------------------------------------
  // Submit
  // ---------------------------------------
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("rel_id", customerId);
      formData.append("rel_type", "Customer");
      formData.append("description", values.description);
      formData.append("addedfrom", addedFrom);

      // Normal attachments
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("attachments[]", file.originFileObj);
        }
      });

      // Voice attachment
      // It will only be sent after user has listened to/confirmed
      // the recording.
      if (voiceFile) {
        formData.append("attachments[]", voiceFile);
      }

      const response = await axios.post(
        `${API_BASE_CRM_URL}notes/AddNote`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.Status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.Message,
          confirmButtonColor: "#1B2850",
        });

        form.resetFields();

        setFileList([]);
        removeVoiceRecording();

        if (onSuccess) {
          onSuccess();
        }

        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.Message,
          confirmButtonColor: "#1B2850",
        });
      }
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error?.response?.data?.Message || "Failed to add note",
        confirmButtonColor: "#1B2850",
      });
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------
  // Close drawer
  // ---------------------------------------
  const handleClose = () => {
    if (isRecording) {
      message.warning("Please stop the recording first.");
      return;
    }

    form.resetFields();
    setFileList([]);
    removeVoiceRecording();

    onClose();
  };

  return (
    <Drawer
      placement="bottom"
      height="55%"
      open={open}
      onClose={handleClose}
      destroyOnClose
      closable={false}
      title={
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ width: "100%" }}
        >
          {/* Left side title */}
          <div className="d-flex align-items-center">
            <i
              className="fa-solid fa-note-sticky me-2"
              style={{
                color: "#1B2850",
                fontSize: "18px",
              }}
            ></i>

            <span
              style={{
                color: "#1B2850",
                fontWeight: 600,
              }}
            >
              Add Customer Note
            </span>
          </div>

          {/* Right side X */}
          {/* <Tooltip title="Close">
            <Button
              type="text"
              danger
              icon={<CloseOutlined />}
              onClick={handleClose}
              disabled={isRecording}
              style={{
                fontSize: "18px",
                width: "36px",
                height: "36px",
              }}
            />
          </Tooltip> */}
        </div>
      }
      extra={
        <div className="d-flex gap-2">
          <div
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={handleClose}
            disabled={isRecording}
            style={{
              width: "38px",
              height: "38px",
              background: "#fff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            }}
          >
            <i
              className="fa-solid fa-xmark"
              style={{
                color: "#dc3545",
                fontSize: "16px",
              }}
            />
          </div>

          <div
            // onClick={handleAddReminder}
            style={{
              width: "38px",
              height: "38px",
              background: "#fff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            }}
            type="success"
            htmlType="submit"
            loading={loading}
            disabled={isRecording}
            icon={<CheckOutlined />}
          >
            <i
              className="fa-solid fa-check"
              style={{
                color: "#198754",
                fontSize: "16px",
              }}
            />
          </div>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* ---------------------------------
            Attachment Utility Bar
        ---------------------------------- */}
        <div
          className="d-flex align-items-center justify-content-between p-2 mb-3"
          style={{
            background: "#f5f5f5",
            borderRadius: "8px",
            border: "1px solid #d9d9d9",
          }}
        >
          <div className="d-flex align-items-center">
            {isRecording ? (
              <div className="d-flex align-items-center text-danger">
                <LoadingOutlined className="me-2" />

                <span className="fw-bold me-2">
                  Recording ({recordingTime}s)
                </span>

                <Tooltip title="Stop Recording">
                  <Button
                    type="primary"
                    danger
                    size="small"
                    onClick={stopRecording}
                  >
                    <CloseOutlined />
                  </Button>
                </Tooltip>
              </div>
            ) : (
              <Space size="middle">
                {/* File */}
                <Upload
                  beforeUpload={() => false}
                  fileList={fileList}
                  onChange={handleUploadChange}
                  showUploadList={false}
                  multiple
                >
                  <Tooltip title="Attach File">
                    <Button
                      type="text"
                      icon={
                        <PaperClipOutlined
                          style={{
                            fontSize: "18px",
                            color: "#1B2850",
                          }}
                        />
                      }
                    />
                  </Tooltip>
                </Upload>

                {/* Camera */}
                <Upload
                  beforeUpload={() => false}
                  fileList={fileList}
                  onChange={handleUploadChange}
                  showUploadList={false}
                  accept="image/*"
                  capture="environment"
                >
                  <Tooltip title="Take Photo">
                    <Button
                      type="text"
                      icon={
                        <CameraOutlined
                          style={{
                            fontSize: "18px",
                            color: "#1B2850",
                          }}
                        />
                      }
                    />
                  </Tooltip>
                </Upload>

                {/* Voice */}
                <Tooltip title="Record Voice">
                  <Button
                    type="text"
                    icon={
                      <AudioOutlined
                        style={{
                          fontSize: "18px",
                          color: "#1B2850",
                        }}
                      />
                    }
                    onClick={startRecording}
                  />
                </Tooltip>
              </Space>
            )}
          </div>

          <span
            style={{
              fontSize: "12px",
              color: "#8c8c8c",
            }}
          >
            Add attachment
          </span>
        </div>

        {/* ---------------------------------
            Voice Preview
        ---------------------------------- */}
        {voicePreview && voiceFile && (
          <div
            className="mb-3 p-3"
            style={{
              border: "1px solid #d9d9d9 ",
              borderRadius: "8px",
              background: "#fafafa",
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <PlayCircleOutlined
                  style={{
                    fontSize: "20px",
                    color: "#1B2850",
                    marginRight: "8px",
                  }}
                />

                <span
                  style={{
                    fontWeight: 600,
                    color: "#1B2850",
                  }}
                >
                  Voice Message
                </span>
              </div>

              <Tooltip title="Remove Voice Recording">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={removeVoiceRecording}
                />
              </Tooltip>
            </div>

            {/* Browser audio player */}
            <audio
              controls
              src={voicePreview}
              style={{
                width: "100%",
                height: "40px",
              }}
            />

            <div
              className="mt-2"
              style={{
                fontSize: "12px",
                color: "#8c8c8c",
              }}
            >
              Please listen to the recording before clicking ✓ to save.
            </div>
          </div>
        )}

        {/* ---------------------------------
            Other Attachments
        ---------------------------------- */}
        {fileList.length > 0 && (
          <div
            className="mb-3 p-2"
            style={{
              maxHeight: "65px",
              overflowY: "auto",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Space wrap size={[0, 8]}>
              {fileList.map((file) => (
                <Tag
                  key={file.uid}
                  color="#1B2850"
                  closable
                  onClose={() =>
                    setFileList((prev) =>
                      prev.filter((f) => f.uid !== file.uid),
                    )
                  }
                  style={{
                    borderRadius: "12px",
                    padding: "4px 10px",
                  }}
                >
                  {file.name}
                </Tag>
              ))}
            </Space>
          </div>
        )}

        {/* ---------------------------------
            Notes
        ---------------------------------- */}
        <Form.Item
          name="description"
          rules={[
            {
              required: true,
              message: "Please enter notes",
            },
          ]}
          style={{
            marginBottom: "12px",
          }}
        >
          <TextArea
            rows={5}
            placeholder="Enter customer notes..."
            maxLength={1000}
            showCount
          />
        </Form.Item>

        {/* ---------------------------------
            Bottom Buttons - Icons Only
        ---------------------------------- */}
        {/* <div className="d-flex justify-content-end mt-3">
   
          <Tooltip title="Cancel">
            <Button
              className="me-2"
              danger
              onClick={handleClose}
              disabled={isRecording}
              icon={<CloseOutlined />}
              style={{
                width: "42px",
                height: "38px",
              }}
            />
          </Tooltip>

         
          <Tooltip title="Save Note">
            <Button
              type="success"
              htmlType="submit"
              loading={loading}
              disabled={isRecording}
              icon={<CheckOutlined />}
              style={{
                width: "42px",
                height: "38px",
                borderRadius: "50%",
                background: "#fafbfd",
                borderColor: "#53fa05",
              }}
            />
          </Tooltip>
        </div> */}
      </Form>
    </Drawer>
  );
};

export default AddNote;
